/**
 * Pure formatting helpers for the Cmd+K chart result. No DOM, no Angular — the
 * same formatting the charts MCP App widget applies
 * (`backend/apps/intelligence/mcp-apps/charts/src/charts.helper.ts`), so a chart
 * reads identically in the panel and in an external MCP host.
 */
import { IntelligenceModels } from '@models/intelligence.model';
import Timeframe = IntelligenceModels.Charts.Timeframe;
import XAxisType = IntelligenceModels.Charts.XAxisType;

/** X-axis types whose raw labels are epoch-ms timestamps. */
const TIME_AXIS_TYPES: ReadonlySet<XAxisType> = new Set([
  XAxisType.Time,
  XAxisType.Timestamp,
  XAxisType.Date,
]);

/** Timeframes whose buckets are whole days rather than hours. */
const DAILY_TIMEFRAMES: ReadonlySet<Timeframe> = new Set([
  Timeframe.LastWeek,
  Timeframe.LastMonth,
]);

const TIMEFRAME_TRANSLATION_KEYS: Record<Timeframe, string> = {
  [Timeframe.Last24Hours]: 'CHART_RESULT.TIMEFRAME.LAST_24_HOURS',
  [Timeframe.SameDayLastWeek]: 'CHART_RESULT.TIMEFRAME.SAME_DAY_LAST_WEEK',
  [Timeframe.LastWeek]: 'CHART_RESULT.TIMEFRAME.LAST_WEEK',
  [Timeframe.LastMonth]: 'CHART_RESULT.TIMEFRAME.LAST_MONTH',
};

export function isTimeAxis(xAxisType: XAxisType | undefined): boolean {
  return xAxisType != null && TIME_AXIS_TYPES.has(xAxisType);
}

/** True when the timeframe buckets by day rather than by hour. */
export function isDailyTimeframe(timeframe: Timeframe | undefined): boolean {
  return timeframe != null && DAILY_TIMEFRAMES.has(timeframe);
}

/**
 * Render an x-axis label for display. Time-axis labels arrive as epoch-ms
 * strings and become a date (daily timeframes) or a 24h clock time; every other
 * axis (camera name, alert type) is already human-readable.
 */
export function formatXLabel(
  label: string,
  xAxisType?: XAxisType,
  timeframe?: Timeframe,
  timezone?: string,
): string {
  if (!isTimeAxis(xAxisType)) return label;

  const ts = Number(label);
  if (Number.isNaN(ts)) return label;

  return isDailyTimeframe(timeframe)
    ? formatDateOnly(ts, timezone)
    : formatClockTime(ts, timezone);
}

/** Title-case a snake/kebab identifier, e.g. `alerts_by_hour` → `Alerts By Hour`. */
export function formatIdentifier(value: string): string {
  return value.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Translation key for a timeframe badge, or `null` for an unknown timeframe —
 * the caller then falls back to the title-cased raw value.
 */
export function timeframeTranslationKey(
  timeframe: Timeframe | undefined,
): string | null {
  return timeframe ? (TIMEFRAME_TRANSLATION_KEYS[timeframe] ?? null) : null;
}

/** Date + time in the chart's timezone, falling back to the local zone if it's invalid. */
export function formatDateTime(ts: number, timezone?: string): string {
  return formatInZone(ts, timezone, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Date only, in the chart's timezone, falling back to the local zone if it's invalid. */
export function formatDateOnly(ts: number, timezone?: string): string {
  return formatInZone(ts, timezone, { month: 'short', day: 'numeric' });
}

/** 24h clock time, in the chart's timezone, falling back to the local zone if it's invalid. */
export function formatClockTime(ts: number, timezone?: string): string {
  return formatInZone(ts, timezone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** The header's "start — end" range line for a chart's timeRange. */
export function formatTimeRange(
  timeRange: IntelligenceModels.Charts.TimeRange | undefined,
  timeframe?: Timeframe,
): string {
  if (!timeRange?.start || !timeRange?.end) return '';
  const format = isDailyTimeframe(timeframe) ? formatDateOnly : formatDateTime;
  return `${format(timeRange.start, timeRange.timezone)} — ${format(
    timeRange.end,
    timeRange.timezone,
  )}`;
}

/**
 * `Intl` rejects an unknown time zone by throwing, and the zone travels with
 * server data — so an unusable one degrades to the viewer's local zone rather
 * than breaking the render.
 */
function formatInZone(
  ts: number,
  timezone: string | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  try {
    return new Date(ts).toLocaleString('en-US', { ...options, timeZone: timezone });
  } catch {
    return new Date(ts).toLocaleString('en-US', options);
  }
}
